import React, { useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Package, Upload, Download, Trash2, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiErr, API } from "@/lib/api";

const fmtSize = (b) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(1)} KB`;

export default function AdminUpdates() {
  const [patches, setPatches] = useState(null);
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = () => adminApi.get("/admin/patches").then((r) => setPatches(r.data)).catch(() => setPatches([]));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f || !version.trim()) { toast.error("Version and a file are required"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("version", version); fd.append("notes", notes); fd.append("visible_to_clients", visible); fd.append("file", f);
      await adminApi.post("/admin/patches", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Patch ${version} uploaded`); setVersion(""); setNotes(""); setVisible(false); if (fileRef.current) fileRef.current.value = ""; load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleVis = async (p) => {
    try {
      await adminApi.patch(`/admin/patches/${p.id}`, { visible_to_clients: !p.visible_to_clients });
      toast.success(!p.visible_to_clients ? "Now visible to clients in What's New" : "Hidden from clients");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const download = async (p) => {
    try {
      const res = await adminApi.get(`/admin/patches/${p.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = p.filename; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await adminApi.delete(`/admin/patches/${id}`); toast.success("Removed"); load(); };

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold font-display">Updates & Patches</h1><p className="text-sm text-slate-500">Upload versioned patch packages for your VPS deployment. Stored & downloadable — does not auto-apply code.</p></div>
      <Card className="border shadow-none p-5 grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div><Label>Version</Label><Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g. 1.2.0" data-testid="patch-version" /></div>
          <div><Label>Release notes</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What changed in this patch..." data-testid="patch-notes" /></div>
        </div>
        <div className="space-y-3">
          <div><Label>Patch file (.zip, max 60MB)</Label><input ref={fileRef} type="file" accept=".zip,.tar,.gz,.tgz" className="block w-full text-sm mt-1" data-testid="patch-file" /></div>
          <div className="flex items-center justify-between rounded-lg border p-2.5"><div className="flex items-center gap-2 text-sm"><Eye className="w-4 h-4 text-slate-400" />Show in clients' "What's New"</div><Switch checked={visible} onCheckedChange={setVisible} data-testid="patch-visible-toggle" /></div>
          <Button onClick={upload} disabled={busy} style={{ background: "#7C3AED" }} data-testid="patch-upload">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}Upload Patch</Button>
        </div>
      </Card>
      <Card className="border shadow-none">
        <div className="p-5 border-b"><h3 className="font-semibold font-display text-sm">Patch History</h3></div>
        {!patches ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
          patches.length === 0 ? <div className="p-10 text-center text-sm text-slate-400" data-testid="patches-empty">No patches uploaded yet.</div> :
            <div className="divide-y">{patches.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-5 py-3" data-testid={`patch-row-${p.id}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(124,58,237,0.1)" }}><Package className="w-4 h-4" style={{ color: "#7C3AED" }} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">v{p.version} <span className="text-slate-400 font-normal">· {p.filename} · {fmtSize(p.size)}</span></div>
                  {p.notes && <div className="text-xs text-slate-500 truncate">{p.notes}</div>}
                  <div className="text-[11px] text-slate-400">{(p.created_at || "").slice(0, 16).replace("T", " ")} · {p.uploaded_by}</div>
                </div>
                <button onClick={() => toggleVis(p)} title={p.visible_to_clients ? "Visible to clients — click to hide" : "Hidden — click to show in What's New"} className={p.visible_to_clients ? "text-emerald-500 hover:text-emerald-600" : "text-slate-300 hover:text-slate-500"} data-testid={`patch-vis-${p.id}`}>{p.visible_to_clients ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                <button onClick={() => download(p)} className="text-slate-400 hover:text-indigo-600" data-testid={`patch-download-${p.id}`}><Download className="w-4 h-4" /></button>
                <button onClick={() => del(p.id)} className="text-slate-400 hover:text-red-500" data-testid={`patch-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}</div>}
      </Card>
    </div>
  );
}
